import { ArrowRightIcon } from '@heroicons/react/outline';
import React, { useCallback, useEffect, useState, VFC } from 'react';
import { TextInput } from 'src/common/form/text-input';
import { IconButton } from 'src/common/icon-button';
import { Journey } from 'src/common/interfaces/journey.interface';
import { Tag } from 'src/common/interfaces/tag.interface';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { MultiSelectItem, MultiSelectWithSearch } from 'src/common/multi-select/with-search';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { CheckmarkRegularIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';
import {
  apiAddTagToJourney,
  apiCreateOrganizationTag,
  apiGetAllOrganizationTags,
  apiRemoveTagFromJourney,
} from 'src/utils/journeyApi';
import { useJourneys } from 'src/store/journeys';
import { useCurrentTags } from 'src/store/tags';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

interface DashboardHomeTagsPanelProps
  extends Pick<
    JourneyMenuPropsInterface,
    'openRef' | 'closeRef' | 'placement' | 'offset' | 'renderOrigin' | 'onOpen' | 'onClose'
  > {
  journeyTags: string[];
  onBack: () => void;
  journey: Journey;
}

export const DashboardHomeTagsPanel: VFC<DashboardHomeTagsPanelProps> = ({
  journeyTags,
  openRef,
  closeRef,
  renderOrigin,
  placement,
  journey,
  onClose,
  onBack,
  onOpen,
}) => {
  const { currentOrganization } = useCurrentOrganization();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const { journeys, setJourneys } = useJourneys();
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { checkFeature, showUpgradeModal } = useFeatureRestrictions();

  const { setCurrentTags } = useCurrentTags();
  const [fullTagsList, setFullTagsList] = useState<MultiSelectItem[]>([]);
  const [listedTags, setListedTags] = useState<MultiSelectItem[]>([]);
  const [selectedOptions, setSelectedOptions] = useState(
    journeyTags
      .map((selectedTag: string) => {
        return listedTags.find((option) => option.value === selectedTag);
      })
      .filter((option) => option !== undefined)
  );

  const fetchTags = useCallback(async () => {
    const tagsResponse = await apiGetAllOrganizationTags(currentOrganization.id);
    const formattedTags = tagsResponse?.tags?.map((item: Tag) => {
      return { value: String(item.id), label: item.name };
    });
    setFullTagsList(formattedTags);
    setListedTags(formattedTags);
    setCurrentTags(tagsResponse.tags);
    setSelectedOptions(
      journeyTags
        .map((selectedTag: string) => {
          return formattedTags.find((option: { label: string; value: string }) => option.value === selectedTag);
        })
        .filter((option) => option !== undefined)
    );
  }, [currentOrganization.id, journeyTags]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const [newTagInput, setNewTagInput] = useState<string | null>(null);

  // Custom onOpen handler that checks feature access
  const handleOpen = () => {
    if (!checkFeature('tags_and_filters')) {
      showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to manage tags');
      return;
    }
    // If feature is allowed, proceed with normal open behavior
    if (onOpen) {
      onOpen();
    }
  };

  const onSearchInputChanged = (searchInput: string) => {
    if (!searchInput) {
      setListedTags([...fullTagsList]);
      return;
    }
    setListedTags(fullTagsList.filter((tag) => tag.label.toLowerCase().includes(searchInput)));
  };

  const addJourneyTag = async (journeyUUID: string, tagId: string, tag?: Tag) => {
    await apiAddTagToJourney(journeyUUID, tagId).then(() => {
      const newSelected = tag
        ? [...selectedOptions, { value: `${tag.id}`, label: tag.name }]
        : (() => {
            const foundTag = listedTags.find((option) => option?.value === tagId);
            return foundTag ? [...selectedOptions, foundTag] : selectedOptions;
          })();
      setSelectedOptions(newSelected);
      const updatedJourneys = journeys.map((item: Journey) => {
        if (item.uuid === journey.uuid) {
          return {
            ...item,
            tags: newSelected.map((option) => {
              return { id: option?.value, name: option?.label };
            }),
          };
        }
        return item;
      });
      setJourneys(updatedJourneys, subscriptionPlanType);
    });
  };

  const removeJourneyTag = async (journeyUUID: string, tagId: string) => {
    await apiRemoveTagFromJourney(journeyUUID, tagId).then(() => {
      const newSelected = selectedOptions.filter((option) => String(option?.value) !== tagId);
      setSelectedOptions(newSelected);
      const updatedJourneys = journeys.map((item: Journey) => {
        if (item.uuid === journey.uuid) {
          return {
            ...item,
            tags: newSelected.map((option) => {
              return { id: option?.value, name: option?.label };
            }),
          };
        }
        return item;
      });
      setJourneys(updatedJourneys, subscriptionPlanType);
    });
  };

  const onItemSelected = (selectedItem: MultiSelectItem) => {
    // Check if user can access tags (Grow and Scale plans)
    if (!checkFeature('tags_and_filters')) {
      showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to manage tags');
      return;
    }
    
    if (selectedOptions.find((selected) => selected?.value === `${selectedItem.value}`)) {
      removeJourneyTag(journey.uuid, selectedItem.value);
    } else {
      addJourneyTag(journey.uuid, selectedItem.value);
    }
  };

  const saveNewTag = async () => {
    if (newTagInput?.length && newTagInput?.length > 0 && currentOrganization?.id) {
      await apiCreateOrganizationTag(currentOrganization.id, newTagInput)
        .then(({ tag }: { tag: { id: number; name: string } }) => {
          fetchTags();
          addJourneyTag(journey.uuid, `${tag.id}`, tag);
          setSuccessNotification('Tag created successfully');
        })
        .catch(() => {
          setErrorNotification('Something went wrong creating tag');
        });
    }
    setNewTagInput('');
  };

  return (
    <MultiSelectWithSearch
      name='journey-tags'
      searchPlaceholder='Search for tags'
      items={listedTags}
      placement={placement}
      selectedItems={[]}
      className='pb-0'
      openRef={openRef}
      onOpen={handleOpen}
      onClose={onClose}
      onBack={onBack}
      hasBackButton
      closeRef={closeRef}
      hideActionOnNotFound
      renderOrigin={renderOrigin}
      renderNotFound={() => <span>No results found</span>}
      onSearchInputChange={onSearchInputChanged}
      onItemSelect={onItemSelected}
      renderListItem={(item, onClick) => {
        const isItemSelected = selectedOptions.find((option) => option?.value === item.value);
        return (
          <div className='flex cursor-pointer items-center hover:opacity-80' onClick={onClick}>
            {isItemSelected && <CheckmarkRegularIcon className='shrink-0' />}
            <div className={isItemSelected ? 'ml-2' : 'ml-7'}>{item.label}</div>
          </div>
        );
      }}
      renderFooter={() => (
        <>
          <TextInput
            id={`new-tag`}
            onChange={(e: any) => {
              e.preventDefault();
              setNewTagInput(e.target.value);
            }}
            value={newTagInput || ''}
            hideOptional
            trailingAddon={
              <IconButton onClick={saveNewTag}>
                <ArrowRightIcon className='w-5 h-5' />
              </IconButton>
            }
            inputProps={{
              type: 'text',
              placeholder: 'New Tag',
            }}
          />
        </>
      )}
    />
  );
};
